import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  SlidersHorizontal,
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  ListChecks,
  Megaphone,
  CalendarClock,
  History,
  Loader2,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Skeleton } from '@easyflow/ui-frontend/components/ui/skeleton-loader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { apiClient as api } from '@/lib/api';
import {
  formatTriggerLabel,
  triggerBadgeClass,
  triggerAccentColor,
  formatRelative,
  formatAbsolute,
  fmtDuration,
  getActionNodes,
  interactsWithTasks,
  buildNodesSummary,
  nodeLabel,
  runStatusBadge,
} from './workflow-shared';
import type { WorkflowNode } from '@/pages/crm/shared/types';

import type { WorkflowListItem, WorkflowSavedRun } from '@easyflow/shared';
export type WorkflowManageItem = WorkflowListItem;

const RUNS_PAGE_SIZE = 20;

interface WorkflowsManageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflows: WorkflowManageItem[];
  loading?: boolean;
  togglingIds?: ReadonlySet<string>;
  onActivate: (id: string, nextActive: boolean) => void | Promise<void>;
  onCreate: () => void;
  canCreate: boolean;
}

type StatusFilter = 'all' | 'active' | 'inactive';

export function WorkflowsManageDialog({
  open,
  onOpenChange,
  workflows,
  loading = false,
  togglingIds = new Set<string>(),
  onActivate,
  onCreate,
  canCreate,
}: WorkflowsManageDialogProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setExpandedId(null);
      setSearch('');
      setDebouncedSearch('');
      setStatusFilter('all');
    }
  }, [open]);

  const filtered = useMemo(() => {
    return workflows.filter((w) => {
      if (debouncedSearch && !w.name.toLowerCase().includes(debouncedSearch)) return false;
      if (statusFilter === 'active' && !w.is_active) return false;
      if (statusFilter === 'inactive' && w.is_active) return false;
      return true;
    });
  }, [workflows, debouncedSearch, statusFilter]);

  const activeCount = useMemo(() => workflows.filter((w) => w.is_active).length, [workflows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[min(95vw,960px)] max-h-[88vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-warning/15">
                  <SlidersHorizontal className="h-5 w-5 text-warning" />
                </span>
                Gestione workflow
                <Badge variant="secondary" className="ml-1 font-normal">
                  {activeCount}/{workflows.length} attivi
                </Badge>
              </DialogTitle>
              <DialogDescription className="mt-1">
                Accendi o spegni i workflow e scopri cosa fanno.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-3 border-b bg-muted/30 flex flex-wrap items-center gap-2 shrink-0">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome…"
              aria-label="Cerca definizioni workflow"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <div className="inline-flex rounded-md border bg-background p-0.5">
            {(['all', 'active', 'inactive'] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={statusFilter === s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
                  statusFilter === s
                    ? 'bg-warning/15 text-warning'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {s === 'all' ? 'Tutti' : s === 'active' ? 'Attivi' : 'Disattivi'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))
          ) : workflows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="h-16 w-16 rounded-full bg-warning/10 flex items-center justify-center mb-4">
                <SlidersHorizontal className="h-8 w-8 text-warning" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Nessun workflow ancora</h3>
              <p className="text-sm text-muted-foreground max-w-sm mb-5">
                Crea il primo workflow per automatizzare un flusso di lavoro, poi torna qui per accenderlo o spegnerlo.
              </p>
              <Button disabled={!canCreate} onClick={onCreate} className="gap-1.5 bg-warning text-warning-foreground hover:bg-warning/90">
                <Plus className="h-4 w-4" />
                Crea il primo workflow
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <h3 className="text-base font-semibold mb-1">Nessun risultato</h3>
              <p className="text-sm text-muted-foreground max-w-sm">Prova a modificare la ricerca o i filtri.</p>
            </div>
          ) : (
            filtered.map((wf) => (
              <WorkflowManageRow
                key={wf.id}
                workflow={wf}
                expanded={expandedId === wf.id}
                toggling={togglingIds.has(wf.id)}
                onToggleExpand={() => setExpandedId((prev) => (prev === wf.id ? null : wf.id))}
                onActivate={onActivate}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface WorkflowManageRowProps {
  workflow: WorkflowManageItem;
  expanded: boolean;
  toggling: boolean;
  onToggleExpand: () => void;
  onActivate: (id: string, nextActive: boolean) => void | Promise<void>;
}

function WorkflowManageRow({ workflow, expanded, toggling, onToggleExpand, onActivate }: WorkflowManageRowProps) {
  const accent = workflow.is_active ? triggerAccentColor(workflow.trigger_type) : '#64748b';
  const actionNodes = getActionNodes(workflow.nodes);
  const hasTasks = interactsWithTasks(workflow.nodes);
  const campaigns = workflow.linked_campaigns ?? [];
  const runCount = Number(workflow.run_count ?? 0);
  const summary =
    workflow.description && workflow.description.trim()
      ? workflow.description
      : buildNodesSummary(workflow.trigger_type, workflow.nodes);

  return (
    <div
      className={`rounded-xl overflow-hidden bg-card border transition-colors ${
        expanded ? 'border-primary/40' : 'hover:border-primary/30'
      }`}
    >
      <div className="h-[3px] w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}80)` }} />
      <div className="p-3">
        <Button variant="ghost" size="sm" aria-expanded={expanded} onClick={onToggleExpand}>Dettagli {workflow.name}</Button>
        <div className="flex items-start gap-3">
          <div
            className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${
              workflow.is_active ? 'bg-success' : 'bg-muted-foreground/40'
            }`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm break-words" title={workflow.name}>
                {workflow.name}
              </span>
              <Badge variant="outline">{workflow.managed_by_org_id ? 'Gestione insegna' : 'Gestione tenant'}</Badge>
                <Badge variant="secondary" className="text-[10px] px-1.5">
                v{workflow.version}
              </Badge>
              <Badge variant="outline" className={`text-[10px] px-2 py-0.5 font-medium ${triggerBadgeClass(workflow.trigger_type)}`}>
                {formatTriggerLabel(workflow.trigger_type)}
              </Badge>
              {workflow.is_active ? (
                <Badge className="text-[10px] px-2 py-0.5 bg-success/10 text-success border-success/20">
                  Definizione attiva
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] px-2 py-0.5 text-muted-foreground">
                  Definizione disabilitata
                </Badge>
              )}
            </div>

            <p className="text-xs text-muted-foreground mt-1 line-clamp-2" title={summary}>
              {summary}
            </p>

            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {actionNodes.slice(0, 4).map((n, i) => (
                <Badge key={i} variant="outline" className="text-[10px] text-muted-foreground">
                  {nodeLabel(n)}
                </Badge>
              ))}
              {actionNodes.length > 4 && (
                <span className="text-[10px] text-muted-foreground">+{actionNodes.length - 4}</span>
              )}
            </div>

            <div className="flex items-center gap-x-3 gap-y-1 mt-2 flex-wrap text-[11px] text-muted-foreground">
              <span>{workflow.admission_scope === 'tenant' ? 'Autonomo tenant' : workflow.admission_scope === 'campaigns' ? 'Campagne collegate' : 'Ambito da confermare'}</span>
              <span>Definizione generale {workflow.is_active ? 'abilitata' : 'disabilitata'}: {workflow.effective.message}</span>
              {campaigns.length > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <Megaphone className="h-3 w-3 text-warning" />
                  {campaigns.slice(0, 2).map((c) => c.name).join(', ')}
                  {campaigns.length > 2 ? ` +${campaigns.length - 2}` : ''}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <Megaphone className="h-3 w-3" />
                  Nessuna campagna
                </span>
              )}
              {hasTasks && (
                <span className="inline-flex items-center gap-1 text-primary">
                  <ListChecks className="h-3 w-3" />
                  Task / ToDo
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <History className="h-3 w-3" />
                {runCount} esecuzioni totali della definizione
              </span>
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-3 w-3" />
                Ultima: {formatRelative(workflow.last_run_at)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={workflow.is_active}
              disabled={toggling || !workflow.capabilities.can_toggle_global}
              aria-label={`Abilitazione generale di ${workflow.name}, interessa tutte le campagne collegate`}
              onCheckedChange={(next) => onActivate(workflow.id, next)}
            />
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <WorkflowManageDetail workflow={workflow} actionNodes={actionNodes} />
      )}
    </div>
  );
}

interface WorkflowManageDetailProps {
  workflow: WorkflowManageItem;
  actionNodes: WorkflowNode[];
}

function WorkflowManageDetail({ workflow, actionNodes }: WorkflowManageDetailProps) {
  const [runs, setRuns] = useState<WorkflowSavedRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [runsError, setRunsError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const campaigns = workflow.linked_campaigns ?? [];

  const fetchRuns = useCallback(
    async (nextOffset: number) => {
      try {
        setLoadingRuns(true);
        setRunsError(false);
        const res = await api.getWorkflowRunsPage(workflow.id, RUNS_PAGE_SIZE, nextOffset);
        setRuns(prev => Array.from(new Map((nextOffset === 0 ? res.runs : [...prev, ...res.runs]).map(run => [run.id, run])).values()));
        setHasMore(res.offset + res.limit < res.total);
        setOffset(res.offset + res.limit);
      } catch {
        setRunsError(true);
      } finally {
        setLoadingRuns(false);
      }
    },
    [workflow.id],
  );

  useEffect(() => {
    fetchRuns(0);
  }, [fetchRuns]);

  return (
    <div className="border-t bg-muted/30 px-4 py-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg bg-card border p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Creato</p>
          <p className="text-xs">{formatAbsolute(workflow.created_at)}</p>
          <p className="text-[11px] text-muted-foreground">{formatRelative(workflow.created_at)}</p>
        </div>
        <div className="rounded-lg bg-card border p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Ultimo aggiornamento</p>
          <p className="text-xs">{formatAbsolute(workflow.updated_at)}</p>
          <p className="text-[11px] text-muted-foreground">{formatRelative(workflow.updated_at)}</p>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold mb-1.5">Cosa fa</p>
        <p className="text-xs text-muted-foreground">
          {workflow.description && workflow.description.trim()
            ? workflow.description
            : buildNodesSummary(workflow.trigger_type, workflow.nodes)}
        </p>
      </div>

      <div>
        <p className="text-xs font-semibold mb-1.5">Flusso</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className={`text-[10px] px-2 py-0.5 font-medium ${triggerBadgeClass(workflow.trigger_type)}`}>
            {formatTriggerLabel(workflow.trigger_type)}
          </Badge>
          {actionNodes.map((n, i) => (
            <span key={n.id || i} className="inline-flex items-center gap-1.5">
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                {nodeLabel(n)}
              </Badge>
            </span>
          ))}
          {actionNodes.length === 0 && (
            <span className="text-[11px] text-muted-foreground">Nessuna azione configurata</span>
          )}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold mb-1.5">Collegamenti campagna</p>
        {campaigns.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">Nessuna campagna collegata.</p>
        ) : (
          <div className="flex items-center gap-1.5 flex-wrap">
            {campaigns.map((c) => (
              <Badge key={c.id} variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/20">
                <Megaphone className="h-3 w-3 mr-1" />
                {c.name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold mb-1.5">Log esecuzioni</p>
        {loadingRuns && runs.length === 0 ? (
          <div className="space-y-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-lg" />
            ))}
          </div>
        ) : runsError ? (
          <div className="flex items-center gap-2 text-xs text-destructive py-3">
            <AlertCircle className="h-4 w-4" />
            Impossibile caricare le esecuzioni.
            <button className="underline text-foreground hover:text-muted-foreground" onClick={() => fetchRuns(0)}>
              Riprova
            </button>
          </div>
        ) : runs.length === 0 ? (
          <p className="text-[11px] text-muted-foreground py-2">Nessuna esecuzione registrata per questo workflow.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto rounded-lg border divide-y">
            {runs.map((run) => {
              const duration =
                run.completed_at && run.started_at
                  ? Math.round(new Date(run.completed_at).getTime() - new Date(run.started_at).getTime())
                  : null;
              const durationLabel = fmtDuration(duration);
              return (
                <div key={run.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="shrink-0">{runStatusBadge(run.status)}</div>
                  <div className="flex-1 min-w-0 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                    <span>{formatRelative(run.started_at)}</span>
                    <span>Versione {run.provenance.definition_version ?? 'ignota'} · {run.provenance.kind === 'unknown_legacy' ? 'Origine storica ignota'
                      : run.provenance.campaign_id ? campaigns.find(c => c.id === run.provenance.campaign_id)?.name ?? 'Campagna registrata, nome non disponibile' : 'Autonomo tenant'}</span>
                    {durationLabel && <span>{durationLabel}</span>}
                    {run.error_message && (
                      <span className="text-destructive truncate max-w-[220px]" title={run.error_message}>
                        {run.error_message}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <div className="px-3 py-2 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => fetchRuns(offset)}
                  disabled={loadingRuns}
                >
                  {loadingRuns ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                  Carica altri
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
